import React, { useRef } from "react";
import { useI18n } from "../i18n/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SystemDiagram from "../components/SystemDiagram";
import WaitlistForm from "../components/WaitlistForm";
import Chatbot from "../components/Chatbot";
import { ArrowRight } from "@phosphor-icons/react";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-24 md:py-32 lg:py-40 ${className}`}>
    <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">{children}</div>
  </section>
);

const Overline = ({ children }) => <div className="overline mb-6">{children}</div>;

export default function Home() {
  const { t } = useI18n();
  const waitlistRef = useRef(null);

  const goWaitlist = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="grain bg-ivory min-h-screen relative">
      <Header onCta={goWaitlist} />

      {/* HERO */}
      <section className="relative pt-40 pb-28 md:pt-52 md:pb-40">
        <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="max-w-4xl">
            <Overline>{t.hero.overline}</Overline>
            <h1
              data-testid="hero-title"
              className="font-headings font-normal tracking-tight text-charcoal text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] animate-fade-up"
            >
              {t.hero.title}
            </h1>
            <p className="mt-10 font-body text-lg md:text-xl text-charcoal/70 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.1s" }}>
              {t.hero.subtitle}
            </p>
            <p className="mt-6 font-body text-base text-charcoal/55 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
              {t.hero.body}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <button
                data-testid="hero-cta-primary"
                onClick={goWaitlist}
                className="group bg-charcoal text-ivory hover:bg-secondary transition-colors duration-300 px-9 py-4 font-mono uppercase tracking-widest text-xs flex items-center justify-center gap-3"
              >
                {t.hero.ctaPrimary}
                <ArrowRight size={14} weight="thin" className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={goWaitlist}
                className="border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory transition-colors duration-300 px-9 py-4 font-mono uppercase tracking-widest text-xs"
              >
                {t.hero.ctaSecondary}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* IS NOT */}
      <Section id="concept" className="border-t border-habitus-border">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Overline>{t.isNot.overline}</Overline>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
              {t.isNot.title}
            </h2>
            <p className="mt-8 font-body text-base text-charcoal/60 leading-relaxed max-w-md">
              {t.isNot.conclusion}
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-habitus-border border border-habitus-border">
            {t.isNot.items.map((it, i) => (
              <div key={i} data-testid={`isnot-item-${i}`} className="bg-ivory p-8 flex flex-col gap-3">
                <span className="font-mono text-[0.65rem] uppercase tracking-widest text-brass-muted">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-body font-medium text-charcoal">{it.k}</span>
                <span className="font-body text-sm text-charcoal/55 leading-relaxed">{it.v}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* PROBLEM */}
      <Section id="problem" className="bg-charcoal text-ivory">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-6" style={{ color: "rgba(244,240,235,0.5)" }}>{t.problem.overline}</div>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-ivory">
              {t.problem.title}
            </h2>
            <p className="mt-8 font-body text-base text-ivory/60 leading-relaxed max-w-md">{t.problem.intro}</p>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-col">
              {t.problem.points.map((p, i) => (
                <div key={i} data-testid={`problem-point-${i}`} className="flex items-baseline gap-6 py-6 border-b border-dark-border">
                  <span className="font-mono text-xs text-brass shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-body text-lg text-ivory/85">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* SOLUTION */}
      <Section id="solution" className="border-t border-habitus-border">
        <div className="max-w-4xl">
          <Overline>{t.solution.overline}</Overline>
          <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05] tracking-tight">
            {t.solution.title}
          </h2>
          <p className="mt-10 font-body text-lg md:text-xl text-charcoal/65 leading-relaxed max-w-3xl">
            {t.solution.body}
          </p>
        </div>
      </Section>

      {/* SYSTEM DIAGRAM */}
      <Section id="system" className="bg-habitus-muted/40 border-t border-b border-habitus-border">
        <div className="text-center mb-16">
          <Overline>{t.diagram.overline}</Overline>
          <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl text-charcoal tracking-tight">{t.diagram.title}</h2>
        </div>
        <SystemDiagram />
      </Section>

      {/* PRODUCT LAYERS */}
      <Section id="layers">
        <div className="mb-16">
          <Overline>{t.layers.overline}</Overline>
          <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl text-charcoal tracking-tight">{t.layers.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-habitus-border border border-habitus-border">
          {t.layers.items.map((l, i) => (
            <div
              key={l.n}
              data-testid={`layer-card-${i}`}
              className="bg-ivory p-8 lg:p-10 flex flex-col group hover:bg-habitus-muted/50 transition-colors duration-500 min-h-[220px]"
            >
              <span className="font-mono text-xs text-brass-muted mb-8">{l.n}</span>
              <h3 className="font-body text-lg font-medium text-charcoal mb-3 group-hover:text-brass-muted transition-colors">{l.t}</h3>
              <p className="font-body text-sm text-charcoal/55 leading-relaxed">{l.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW LIFE CHANGES */}
      <Section className="bg-charcoal text-ivory">
        <div className="max-w-3xl mb-16">
          <div className="overline mb-6" style={{ color: "rgba(244,240,235,0.5)" }}>{t.changes.overline}</div>
          <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl text-ivory leading-tight tracking-tight">{t.changes.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-dark-border border border-dark-border">
          {t.changes.items.map((it, i) => (
            <div key={i} data-testid={`change-item-${i}`} className="bg-charcoal p-8 lg:p-10 flex flex-col">
              <span className="font-mono text-xs text-brass mb-8">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-headings text-2xl text-ivory mb-3">{it.t}</h3>
              <p className="font-body text-sm text-ivory/55 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* MEMBERSHIP */}
      <Section id="membership" className="border-t border-habitus-border">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Overline>{t.membership.overline}</Overline>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">{t.membership.title}</h2>
            <p className="mt-8 font-body text-base text-charcoal/60 leading-relaxed max-w-md">{t.membership.body}</p>
            <p className="mt-6 font-mono text-[0.7rem] uppercase tracking-widest text-brass-muted">{t.membership.note}</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-habitus-border border border-habitus-border">
            {t.membership.types.map((m, i) => (
              <div key={i} data-testid={`membership-type-${i}`} className="bg-ivory p-8 lg:p-10 flex flex-col">
                <span className="font-mono text-[0.65rem] uppercase tracking-widest text-brass-muted mb-6">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-headings text-2xl text-charcoal mb-3">{m.t}</h3>
                <p className="font-body text-sm text-charcoal/55 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* WAITLIST */}
      <Section id="waitlist" className="bg-habitus-muted/40 border-t border-habitus-border">
        <div ref={waitlistRef} className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Overline>{t.waitlist.overline}</Overline>
            <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl text-charcoal tracking-tight">{t.waitlist.title}</h2>
            <p className="mt-6 font-body text-base text-charcoal/60 max-w-xl mx-auto leading-relaxed">{t.waitlist.subtitle}</p>
          </div>
          <WaitlistForm />
        </div>
      </Section>

      <Footer />
      <Chatbot onOpenForm={goWaitlist} />
    </div>
  );
}
